import "./PeopleModal.scss";
import { useState, useRef, useEffect } from "react";
import TeamItem from "./TeamItem";
import PersonItem from "./PersonItem";
import ColorPicker from "../../../ColorPicker/ColorPicker";
import FlowTextInput from "../../../FlowTextInput/FlowTextInput";
import Spinner from "../../../Spinner/Spinner";
import { normalizeColor } from "../../../../utils/colors";
import {
  getOrganizationMembers,
  updateOrganizationMemberRole,
  removeOrganizationMember,
  sendOrganizationInvite,
} from "../../../../api/organizations/organizations";
import {
  getTeams,
  getTeamById,
  createTeam,
  updateTeam,
  deleteTeam,
} from "../../../../api/teams/teams";
import { sendProjectInvite } from "../../../../api/projects";
import useAuthStore from "../../../../store/authStore";
import { useOrganizationRealtime } from "../../../../hooks/useRealtime";

function PeopleModal({ onClose }) {
  const [activeTab, setActiveTab] = useState("people");
  const [selectedItem, setSelectedItem] = useState(null); // Track selected team or person
  const [rightPanelContent, setRightPanelContent] = useState(null); // 'team', 'person', 'addTeam', 'invitePerson', 'removePerson'
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedTeamColor, setSelectedTeamColor] = useState("denim");
  const [teamName, setTeamName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]); // Track selected team members
  const [contentReady, setContentReady] = useState(false); // Delay content rendering for smooth animation
  const [isAdmin, setIsAdmin] = useState(false); // Track admin status for person
  const [peopleData, setPeopleData] = useState([]); // Store fetched people data
  const [teamsData, setTeamsData] = useState([]); // Store fetched teams data
  const [loading, setLoading] = useState(true); // Loading state
  const [teamsLoading, setTeamsLoading] = useState(true); // Teams loading state
  const [personToRemove, setPersonToRemove] = useState(null); // Track person to be removed
  const [teamToRemove, setTeamToRemove] = useState(null); // Track team to be removed
  const [selectedPerson, setSelectedPerson] = useState(null); // Track currently selected person for editing
  // eslint-disable-next-line no-unused-vars
  const [selectedTeam, setSelectedTeam] = useState(null); // Track currently selected team
  const [inviteEmail, setInviteEmail] = useState(""); // Email for invite
  const [inviteNote, setInviteNote] = useState(""); // Note for invite
  const [inviteToCurrentProject, setInviteToCurrentProject] = useState(true); // Invite to current project checkbox
  const [inviteToOrgOnly, setInviteToOrgOnly] = useState(false); // Invite to organization only checkbox
  const [selectedInviteProject, setSelectedInviteProject] = useState(null); // Selected project for invite
  const [inviteTermsAccepted, setInviteTermsAccepted] = useState(false); // Terms acceptance for invite
  const [peopleSearchQuery, setPeopleSearchQuery] = useState(""); // Search query for people
  const [teamsSearchQuery, setTeamsSearchQuery] = useState(""); // Search query for teams
  const [isSendingInvite, setIsSendingInvite] = useState(false); // Loading state for sending invite
  const teamsSearchRef = useRef(null);
  const peopleSearchRef = useRef(null);
  const teamNameInputRef = useRef(null);
  const previousPanelRef = useRef(null); // Track previous panel state

  // Get user and token from auth store
  const { user, token, projects, currentProject, organizations } =
    useAuthStore();
  const currentOrganizationId = user?.currentOrganization;

  // Get current organization details
  const currentOrganization = organizations?.find(
    (org) => org._id === currentOrganizationId,
  );

  // Determine if right panel should be visible
  const isExpanded = rightPanelContent !== null;

  // Sort people by role: admin -> member -> pending
  const sortPeopleByRole = (people) => {
    const roleOrder = { admin: 1, member: 2, pending: 3 };
    return [...people].sort((a, b) => {
      const aRole = a.status === "pending" ? "pending" : a.role;
      const bRole = b.status === "pending" ? "pending" : b.role;
      return (roleOrder[aRole] || 999) - (roleOrder[bRole] || 999);
    });
  };

  // Fetch functions
  const fetchMembers = async () => {
    if (!currentOrganizationId || !token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const members = await getOrganizationMembers(
        currentOrganizationId,
        token,
      );
      setPeopleData(members);
    } catch (error) {
      console.error("Failed to fetch organization members:", error);
      setPeopleData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamsData = async () => {
    if (!currentOrganizationId || !token) {
      setTeamsLoading(false);
      return;
    }

    try {
      setTeamsLoading(true);
      const teams = await getTeams(currentOrganizationId, token);
      setTeamsData(teams);
    } catch (error) {
      console.error("Failed to fetch teams:", error);
      setTeamsData([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  // Fetch organization members on mount
  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationId, token]);

  // Fetch teams on mount or when switching to teams tab
  useEffect(() => {
    fetchTeamsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationId, token]);

  // Real-time updates for teams and members
  useOrganizationRealtime(currentOrganizationId, {
    onTeamCreated: fetchTeamsData,
    onTeamUpdated: fetchTeamsData,
    onTeamDeleted: fetchTeamsData,
    onUserRoleUpdated: fetchMembers,
    onUserInvited: fetchMembers,
    onUserRemoved: fetchMembers,
  });

  // Real-time WebSocket updates for teams and organization members
  useOrganizationRealtime(currentOrganizationId, {
    onTeamCreated: () => {
      fetchTeamsData();
    },
    onTeamUpdated: () => {
      fetchTeamsData();
    },
    onTeamDeleted: () => {
      fetchTeamsData();
    },
    onUserRoleUpdated: () => {
      fetchMembers();
    },
    onUserRemoved: () => {
      fetchMembers();
    },
  });

  const handleTeamClick = async (teamId) => {
    const team = teamsData.find((t) => t.id === teamId);
    if (!team) return;

    setSelectedItem(teamId);
    setSelectedTeam(team);
    setTeamName(team.name);
    // Convert hex color to color name for ColorPicker
    setSelectedTeamColor(normalizeColor(team.color));
    setShowColorPicker(false);
    setRightPanelContent("team");

    // Fetch full team details including members
    try {
      const teamDetails = await getTeamById(teamId, token);
      setSelectedMembers(teamDetails.members || []);
    } catch (error) {
      console.error("Failed to fetch team details:", error);
      setSelectedMembers([]);
    }
  };

  const handleToggleMember = (personId) => {
    setSelectedMembers((prev) =>
      prev.includes(personId)
        ? prev.filter((id) => id !== personId)
        : [...prev, personId],
    );
  };

  const handlePersonClick = (personName) => {
    const person = peopleData.find((p) => p.name === personName);

    // Don't open panel if clicking yourself
    if (person?.id === user?.id || person?.id === user?._id) {
      return;
    }

    setSelectedItem(personName);
    setSelectedPerson(person);
    setIsAdmin(person?.role === "admin");
    setRightPanelContent("person");
  };

  const handleSavePersonRole = async () => {
    if (!selectedPerson || !currentOrganizationId || !token) return;

    try {
      const newRole = isAdmin ? "admin" : "member";

      // Call API to update member role
      await updateOrganizationMemberRole(
        currentOrganizationId,
        selectedPerson.id,
        newRole,
        token,
      );

      // Update local state
      setPeopleData((prev) =>
        prev.map((p) =>
          p.id === selectedPerson.id ? { ...p, role: newRole } : p,
        ),
      );

      // Close the panel
      handleCloseRightPanel();
    } catch (error) {
      console.error("Failed to update person role:", error);
      // TODO: Show error message to user
    }
  };

  const handleAddTeam = () => {
    setSelectedItem(null);
    setTeamName("");
    setSelectedTeamColor("denim");
    setSelectedMembers([]);
    setShowColorPicker(false);
    setRightPanelContent("addTeam");
  };

  const handleSaveNewTeam = async () => {
    if (!teamName.trim() || !currentOrganizationId || !token) return;

    try {
      const newTeam = await createTeam(
        {
          name: teamName,
          color: normalizeColor(selectedTeamColor),
          organizationId: currentOrganizationId,
          members: selectedMembers,
        },
        token,
      );

      // Add new team to local state
      setTeamsData((prev) => [...prev, newTeam]);

      // Close the panel
      handleCloseRightPanel();
    } catch (error) {
      console.error("Failed to create team:", error);
      // TODO: Show error message to user
    }
  };

  const handleSaveTeam = async () => {
    if (!teamName.trim() || !selectedItem || !token) return;

    try {
      const updatedTeam = await updateTeam(
        selectedItem,
        {
          name: teamName,
          color: normalizeColor(selectedTeamColor),
          members: selectedMembers,
        },
        token,
      );

      // Update team in local state
      setTeamsData((prev) =>
        prev.map((team) => (team.id === selectedItem ? updatedTeam : team)),
      );

      // Close the panel
      handleCloseRightPanel();
    } catch (error) {
      console.error("Failed to update team:", error);
      // TODO: Show error message to user
    }
  };

  const handleInvitePerson = () => {
    setSelectedItem(null);
    setInviteEmail("");
    setInviteNote("");
    setInviteToCurrentProject(true);
    setInviteToOrgOnly(false);
    setSelectedInviteProject(null);
    setInviteTermsAccepted(false);
    setRightPanelContent("invitePerson");
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim() || !currentOrganizationId || !token) return;

    setIsSendingInvite(true);

    try {
      // If inviting to organization only
      if (inviteToOrgOnly) {
        await sendOrganizationInvite(
          currentOrganizationId,
          inviteEmail,
          inviteNote,
          token,
        );
      } else {
        // Determine which project to invite to
        const projectToInvite = inviteToCurrentProject
          ? currentProject
          : projects.find(
              (p) =>
                p.id === selectedInviteProject ||
                p._id === selectedInviteProject,
            );

        //   inviteToCurrentProject,
        //   currentProject,
        //   selectedInviteProject,
        //   projectToInvite,
        //   projects,
        // });

        if (projectToInvite) {
          const projectId = projectToInvite.id || projectToInvite._id;

          if (!projectId) {
            throw new Error(
              "Project ID is missing. Please try again or contact support.",
            );
          }

          // Invite to project (which also adds to organization)
          await sendProjectInvite(projectId, inviteEmail, inviteNote, token);
        } else {
          // Fallback to organization invite if no project selected
          await sendOrganizationInvite(
            currentOrganizationId,
            inviteEmail,
            inviteNote,
            token,
          );
        }
      }

      // Refresh members list to show pending invite
      const members = await getOrganizationMembers(
        currentOrganizationId,
        token,
      );
      setPeopleData(members);

      // Close the panel
      handleCloseRightPanel();
    } catch (error) {
      console.error("Failed to send invite:", error);
      console.error("Error details:", {
        message: error.message,
        inviteToOrgOnly,
        inviteToCurrentProject,
        selectedInviteProject,
        currentProjectId: currentProject?.id,
        projectToInviteId: inviteToCurrentProject
          ? currentProject?.id
          : selectedInviteProject,
      });
      // alert(`Failed to send invite: ${error.message}`);
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleRemovePerson = (person) => {
    setPersonToRemove(person);
    setRightPanelContent("removePerson");
  };

  const handleConfirmRemove = async () => {
    if (!personToRemove || !currentOrganizationId || !token) return;

    try {
      console.log("Removing person:", personToRemove.id);

      // Call API to remove person from organization
      await removeOrganizationMember(
        currentOrganizationId,
        personToRemove.id,
        token,
      );

      console.log("Person removed successfully");

      // Remove person from local state
      setPeopleData((prev) => prev.filter((p) => p.id !== personToRemove.id));

      // Close the panel
      handleCloseRightPanel();
      setPersonToRemove(null);
    } catch (error) {
      console.error("Failed to remove person:", error);
      // alert("Failed to remove person: " + (error.message || "Unknown error"));
    }
  };

  const handleRemoveTeam = (team) => {
    setTeamToRemove(team);
    setRightPanelContent("removeTeam");
  };

  const handleConfirmRemoveTeam = async () => {
    if (!teamToRemove || !token) return;

    try {
      // Call API to delete team
      await deleteTeam(teamToRemove.id, token);

      // Remove team from local state
      setTeamsData((prev) => prev.filter((t) => t.id !== teamToRemove.id));

      // Close the panel
      handleCloseRightPanel();
      setTeamToRemove(null);
    } catch (error) {
      console.error("Failed to delete team:", error);
      // TODO: Show error message to user
    }
  };

  const handleCloseRightPanel = () => {
    setRightPanelContent(null);
    setSelectedItem(null);
    setPersonToRemove(null);
    setSelectedPerson(null);
    setSelectedTeam(null);
    // Reset invite states
    setInviteEmail("");
    setInviteNote("");
    setInviteToCurrentProject(true);
    setInviteToOrgOnly(false);
    setSelectedInviteProject(null);
  };

  // Focus search field on mount
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (peopleSearchRef.current) {
        peopleSearchRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Focus search field when switching tabs
  useEffect(() => {
    // Close right panel when switching tabs
    if (rightPanelContent) {
      setRightPanelContent(null);
      setSelectedItem(null);
    }

    const timer = setTimeout(() => {
      if (activeTab === "teams" && teamsSearchRef.current) {
        teamsSearchRef.current.focus();
      } else if (activeTab === "people" && peopleSearchRef.current) {
        peopleSearchRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Focus team name input when team panel opens
  useEffect(() => {
    const wasExpanded = previousPanelRef.current !== null;
    const isExpanding = !wasExpanded && rightPanelContent !== null;

    if (isExpanding) {
      // Panel is opening for the first time - delay content
      setContentReady(false);
      const timer = setTimeout(() => {
        setContentReady(true);
      }, 300); // Wait for slide animation
      previousPanelRef.current = rightPanelContent;
      return () => clearTimeout(timer);
    } else if (rightPanelContent) {
      // Panel is already open, just switching content - show immediately
      setContentReady(true);
      previousPanelRef.current = rightPanelContent;
    } else {
      // Panel is closing
      setContentReady(false);
      previousPanelRef.current = null;
    }
  }, [rightPanelContent]);

  // Focus input after content is ready
  useEffect(() => {
    if (
      contentReady &&
      rightPanelContent === "team" &&
      teamNameInputRef.current
    ) {
      const timer = setTimeout(() => {
        teamNameInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [contentReady, rightPanelContent]);

  return (
    <div className={`people-modal-content ${isExpanded ? "expanded" : ""}`}>
      <div className="title">
        <span>
          {activeTab === "teams"
            ? "Teams in organization: "
            : "People in organization: "}
          <span
            style={{
              color: currentOrganization
                ? normalizeColor(currentOrganization.color)
                : "inherit",
            }}
          >
            "{currentOrganization?.name || "Organization"}"
          </span>
        </span>
        <button className="close-button" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>
      <div className="main-wrapper">
        <div className="left">
          <div
            className={`button ${activeTab === "people" ? "active" : ""}`}
            onClick={() => setActiveTab("people")}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "24px" }}
            >
              groups
            </span>
          </div>
          <div
            className={`button ${activeTab === "teams" ? "active" : ""}`}
            onClick={() => setActiveTab("teams")}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "24px" }}
            >
              family_group
            </span>
          </div>
        </div>
        <div className="right">
          {activeTab === "teams" && (
            <>
              <div className="left-column">
                <div className="search-row">
                  <div className="search-container">
                    <input
                      ref={teamsSearchRef}
                      type="text"
                      className="search-input"
                      placeholder="Search teams..."
                      value={teamsSearchQuery}
                      onChange={(e) => setTeamsSearchQuery(e.target.value)}
                    />
                    <span className="material-symbols-outlined search-icon">
                      search
                    </span>
                  </div>
                </div>
                <div className="list-row">
                  {teamsLoading ? (
                    <div className="loading-message">
                      <Spinner />
                    </div>
                  ) : teamsData.filter((team) =>
                      team.name
                        .toLowerCase()
                        .includes(teamsSearchQuery.toLowerCase()),
                    ).length === 0 ? (
                    <div className="empty-message">No teams found</div>
                  ) : (
                    teamsData
                      .filter((team) =>
                        team.name
                          .toLowerCase()
                          .includes(teamsSearchQuery.toLowerCase()),
                      )
                      .map((team) => (
                        <TeamItem
                          key={team.id}
                          name={team.name}
                          memberCount={team.memberCount}
                          color={normalizeColor(team.color)}
                          onClick={() => handleTeamClick(team.id)}
                          isSelected={selectedItem === team.id}
                          onDelete={() => handleRemoveTeam(team)}
                        />
                      ))
                  )}
                </div>
                <div className="action-row">
                  <button className="add-team-button" onClick={handleAddTeam}>
                    + Add Team
                  </button>
                </div>
              </div>
            </>
          )}
          {activeTab === "people" && (
            <>
              <div className="left-column">
                <div className="search-row">
                  <div className="search-container">
                    <input
                      ref={peopleSearchRef}
                      type="text"
                      className="search-input"
                      placeholder="Search people..."
                      value={peopleSearchQuery}
                      onChange={(e) => setPeopleSearchQuery(e.target.value)}
                    />
                    <span className="material-symbols-outlined search-icon">
                      search
                    </span>
                  </div>
                </div>
                <div className="list-row">
                  {loading ? (
                    <div className="loading-message">
                      <Spinner />
                    </div>
                  ) : sortPeopleByRole(peopleData).filter((person) =>
                      person.name
                        .toLowerCase()
                        .includes(peopleSearchQuery.toLowerCase()),
                    ).length === 0 ? (
                    <div className="empty-message">No members found</div>
                  ) : (
                    sortPeopleByRole(peopleData)
                      .filter((person) =>
                        person.name
                          .toLowerCase()
                          .includes(peopleSearchQuery.toLowerCase()),
                      )
                      .map((person) => (
                        <PersonItem
                          key={person.id}
                          name={person.name}
                          role={
                            person.status === "pending"
                              ? "pending"
                              : person.role
                          }
                          onClick={() => handlePersonClick(person.name)}
                          isSelected={selectedItem === person.name}
                          showDelete={
                            person.id !== user?.id && person.id !== user?._id
                          }
                          onDelete={() => handleRemovePerson(person)}
                          isCurrentUser={
                            person.id === user?.id || person.id === user?._id
                          }
                        />
                      ))
                  )}
                </div>
                <div className="action-row">
                  <button
                    className="invite-person-button"
                    onClick={handleInvitePerson}
                  >
                    Invite Person
                  </button>
                </div>
              </div>
            </>
          )}
          {isExpanded && (
            <div className="right-column">
              <div className="column-content">
                {contentReady && rightPanelContent === "team" && (
                  <div className="team-details">
                    <div className="team-header-row">
                      <div className="color-picker-section">
                        <button
                          className="palette-button"
                          onClick={() => setShowColorPicker(!showColorPicker)}
                          style={{
                            backgroundColor: normalizeColor(selectedTeamColor),
                          }}
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{ color: "white" }}
                          >
                            palette
                          </span>
                          <span
                            className="material-symbols-outlined chevron"
                            style={{ color: "white" }}
                          >
                            {showColorPicker ? "expand_less" : "expand_more"}
                          </span>
                        </button>
                      </div>
                      <input
                        ref={teamNameInputRef}
                        type="text"
                        className="team-name-input"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                      />
                      <button
                        className="close-panel-button"
                        onClick={handleCloseRightPanel}
                      >
                        <span className="material-symbols-outlined">
                          arrow_back
                        </span>
                      </button>
                    </div>
                    {showColorPicker && (
                      <div className="color-picker-wrapper">
                        <ColorPicker
                          selectedColor={selectedTeamColor}
                          onColorSelect={(color) => {
                            setSelectedTeamColor(color);
                            setShowColorPicker(false);
                          }}
                        />
                      </div>
                    )}
                    {!showColorPicker && (
                      <div className="team-members-section">
                        <div className="members-list">
                          {peopleData.map((person) => (
                            <PersonItem
                              key={person.id}
                              name={person.name}
                              role={person.role}
                              onClick={() => handleToggleMember(person.id)}
                              isSelected={selectedMembers.includes(person.id)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="action-buttons">
                      <button
                        className="cancel-button"
                        onClick={handleCloseRightPanel}
                      >
                        Cancel
                      </button>
                      <button className="save-button" onClick={handleSaveTeam}>
                        Save
                      </button>
                    </div>
                  </div>
                )}
                {contentReady && rightPanelContent === "person" && (
                  <div className="person-details">
                    <div className="content-header">
                      <h3>{selectedItem}</h3>
                      <button
                        className="close-panel-button"
                        onClick={handleCloseRightPanel}
                      >
                        <span className="material-symbols-outlined">
                          arrow_back
                        </span>
                      </button>
                    </div>
                    <div className="person-content">
                      <p className="admin-info">
                        When person is admin he can handle people and teams as
                        well as billing
                      </p>
                      <label className="admin-checkbox-label">
                        <input
                          type="checkbox"
                          className="admin-checkbox"
                          checked={isAdmin}
                          onChange={(e) => setIsAdmin(e.target.checked)}
                        />
                        <span>Admin</span>
                      </label>
                    </div>
                    <div className="action-buttons">
                      <button
                        className="cancel-button"
                        onClick={handleCloseRightPanel}
                      >
                        Cancel
                      </button>
                      <button
                        className="save-button"
                        onClick={handleSavePersonRole}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
                {contentReady && rightPanelContent === "addTeam" && (
                  <div className="team-details">
                    <div className="team-header-row">
                      <div className="color-picker-section">
                        <button
                          className="palette-button"
                          onClick={() => setShowColorPicker(!showColorPicker)}
                          style={{
                            backgroundColor: normalizeColor(selectedTeamColor),
                          }}
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{ color: "white" }}
                          >
                            palette
                          </span>
                          <span
                            className="material-symbols-outlined chevron"
                            style={{ color: "white" }}
                          >
                            {showColorPicker ? "expand_less" : "expand_more"}
                          </span>
                        </button>
                      </div>
                      <input
                        ref={teamNameInputRef}
                        type="text"
                        className="team-name-input"
                        placeholder="Enter team name..."
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                      />
                      <button
                        className="close-panel-button"
                        onClick={handleCloseRightPanel}
                      >
                        <span className="material-symbols-outlined">
                          arrow_back
                        </span>
                      </button>
                    </div>
                    {showColorPicker && (
                      <div className="color-picker-wrapper">
                        <ColorPicker
                          selectedColor={selectedTeamColor}
                          onColorSelect={(color) => {
                            setSelectedTeamColor(color);
                            setShowColorPicker(false);
                          }}
                        />
                      </div>
                    )}
                    {!showColorPicker && (
                      <div className="team-members-section">
                        <div className="members-list">
                          {peopleData.map((person) => (
                            <PersonItem
                              key={person.id}
                              name={person.name}
                              role={
                                person.status === "pending"
                                  ? "pending"
                                  : person.role
                              }
                              onClick={() => handleToggleMember(person.id)}
                              isSelected={selectedMembers.includes(person.id)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="action-buttons">
                      <button
                        className="cancel-button"
                        onClick={handleCloseRightPanel}
                      >
                        Cancel
                      </button>
                      <button
                        className="save-button"
                        onClick={handleSaveNewTeam}
                      >
                        Create Team
                      </button>
                    </div>
                  </div>
                )}
                {contentReady && rightPanelContent === "invitePerson" && (
                  <div className="invite-form">
                    <div className="content-header">
                      <h3>Invite Person</h3>
                      <button
                        className="close-panel-button"
                        onClick={handleCloseRightPanel}
                      >
                        <span className="material-symbols-outlined">
                          arrow_back
                        </span>
                      </button>
                    </div>
                    <div className="invite-content">
                      <div className="form-field">
                        <FlowTextInput
                          placeholder="Enter email address..."
                          expandable={false}
                          icon={null}
                          className="email-input"
                          value={inviteEmail}
                          onChange={(value) => setInviteEmail(value)}
                        />
                      </div>
                      <div className="form-field">
                        <textarea
                          className="note-textarea"
                          placeholder="Add a note (optional)..."
                          rows="4"
                          value={inviteNote}
                          onChange={(e) => setInviteNote(e.target.value)}
                        />
                      </div>

                      {/* Invite to Organization Only Checkbox */}
                      <div className="form-field checkbox-field">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={inviteToOrgOnly}
                            onChange={(e) => {
                              setInviteToOrgOnly(e.target.checked);
                              if (e.target.checked) {
                                setInviteToCurrentProject(false);
                              }
                            }}
                          />
                          <span>&nbsp;Invite to organization only</span>
                        </label>
                      </div>

                      {/* Invite to Current Project Checkbox */}
                      {currentProject && (
                        <div className="form-field checkbox-field">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={
                                inviteToCurrentProject && !inviteToOrgOnly
                              }
                              onChange={(e) => {
                                setInviteToCurrentProject(e.target.checked);
                                if (e.target.checked) {
                                  setInviteToOrgOnly(false);
                                  setSelectedInviteProject(null);
                                }
                              }}
                            />
                            <span
                              style={{
                                color: inviteToOrgOnly
                                  ? "var(--disabled-text-color)"
                                  : "inherit",
                              }}
                            >
                              &nbsp;Invite to:
                              <span style={{ color: currentProject.color }}>
                                &nbsp;{currentProject.projectName}
                              </span>
                            </span>
                          </label>
                        </div>
                      )}

                      {/* Project Dropdown */}
                      <div className="form-field">
                        <select
                          className="form-select"
                          value={selectedInviteProject || ""}
                          disabled={
                            inviteToOrgOnly ||
                            !projects ||
                            projects.length === 0
                          }
                          onChange={(e) => {
                            const projectId = e.target.value;
                            setSelectedInviteProject(projectId || null);
                            if (projectId) {
                              setInviteToCurrentProject(false);
                              setInviteToOrgOnly(false);
                            }
                          }}
                        >
                          <option value="">Select another project...</option>
                          {projects
                            ?.filter(
                              (p) =>
                                p.id !== currentProject?.id &&
                                p._id !== currentProject?._id,
                            )
                            .map((project) => (
                              <option key={project.id} value={project.id}>
                                {project.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Terms Acceptance Checkbox - Only for trial/active subscriptions */}
                      {user?.subscription !== "free" &&
                        user?.role !== "superAdmin" && (
                          <div
                            className="form-field checkbox-field"
                            style={{ marginTop: "16px" }}
                          >
                            <label
                              className="checkbox-label"
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "8px",
                                fontSize: "13px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={inviteTermsAccepted}
                                onChange={(e) =>
                                  setInviteTermsAccepted(e.target.checked)
                                }
                                style={{
                                  marginTop: "2px",
                                  cursor: "pointer",
                                  width: "14px",
                                  height: "14px",
                                  flexShrink: 0,
                                }}
                              />
                              <span style={{ color: "#ccc" }}>
                                {user?.subscription === "trial"
                                  ? "I understand that adding a person to my team will add $8/month to my billing when the trial ends."
                                  : "I understand that adding a person to my team will add $8/month to my billing."}
                              </span>
                            </label>
                          </div>
                        )}

                      {isSendingInvite && (
                        <Spinner text="Sending invitation..." />
                      )}
                    </div>
                    <div className="action-row">
                      <button
                        className="send-invite-button"
                        onClick={handleSendInvite}
                        disabled={
                          !inviteEmail.trim() ||
                          isSendingInvite ||
                          (user?.subscription !== "free" &&
                            user?.role !== "superAdmin" &&
                            !inviteTermsAccepted)
                        }
                        style={{
                          opacity:
                            user?.subscription === "free" ||
                            user?.role === "superAdmin" ||
                            inviteTermsAccepted
                              ? 1
                              : 0.5,
                          cursor:
                            user?.subscription === "free" ||
                            user?.role === "superAdmin" ||
                            inviteTermsAccepted
                              ? "pointer"
                              : "not-allowed",
                        }}
                      >
                        Send Invite
                      </button>
                    </div>
                  </div>
                )}
                {contentReady &&
                  rightPanelContent === "removePerson" &&
                  personToRemove && (
                    <div className="remove-person-confirmation">
                      <div className="content-header">
                        <h3>Remove Person</h3>
                        <button
                          className="close-panel-button"
                          onClick={handleCloseRightPanel}
                        >
                          <span className="material-symbols-outlined">
                            arrow_back
                          </span>
                        </button>
                      </div>
                      <div className="confirmation-content">
                        <p className="confirmation-message">
                          Are you sure you want to remove{" "}
                          <strong>{personToRemove.name}</strong> from this
                          organization?
                        </p>
                      </div>
                      <div className="action-buttons">
                        <button
                          className="cancel-button"
                          onClick={handleCloseRightPanel}
                        >
                          Cancel
                        </button>
                        <button
                          className="remove-button"
                          onClick={handleConfirmRemove}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                {contentReady &&
                  rightPanelContent === "removeTeam" &&
                  teamToRemove && (
                    <div className="remove-team-confirmation">
                      <div className="content-header">
                        <h3>Delete Team</h3>
                        <button
                          className="close-panel-button"
                          onClick={handleCloseRightPanel}
                        >
                          <span className="material-symbols-outlined">
                            arrow_back
                          </span>
                        </button>
                      </div>
                      <div className="confirmation-content">
                        <p className="confirmation-message">
                          Are you sure you want to delete the team{" "}
                          <strong>{teamToRemove.name}</strong>?
                        </p>
                      </div>
                      <div className="action-buttons">
                        <button
                          className="remove-button"
                          onClick={handleConfirmRemoveTeam}
                        >
                          Delete
                        </button>
                        <button
                          className="cancel-button"
                          onClick={handleCloseRightPanel}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PeopleModal;
